import { environment } from '@ieEnv/environment';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { PaginatedResponseDto } from 'shared-general-libs/dto/pagination';
import { removeEmptyFields } from '@ieShared/utils/query.utils';
import { ContactsRequestInterface } from '@ieShared/interfaces/contacts.interface';
import {
  ContactResponseDto,
  CreateAnswerDto,
  CreateContactDto,
  CreateSurveyDto,
  GetFormDataResponseDto,
  ManageSurveyParticipantsDto,
  SurveyResponseDto,
  SurveyResponseWithStatsDto,
  UpdateSurveyDto,
  UpdateSurveyEndDateDto,
} from 'shared-general-libs/dto/materiality';
import {
  ParticipantsPaginationInterface,
  SurveyRequestInterface,
} from '@ieShared/interfaces/materiality.interface';
import { ImportanceType } from 'shared-general-libs/type/materiality';
import { map } from 'rxjs/operators';
import {
  AutoAllocateDto,
  BaselineResponseDto,
  BaselineTopicStatisticsDto,
  ConfirmGoalDto,
  CreateBaselineDto,
  CreateGoalDto,
  CreateProjectActivityDto,
  CreateProjectDto,
  CreateProjectExpenseDto,
  CreateProjectMilestoneDto,
  CustomAllocationDto,
  FilterBaselinesDto,
  FilterGoalsDto,
  GoalResponseDto,
  GoalTopicStatisticsDto,
  ProjectActivityResponseDto,
  ProjectExpenseResponseDto,
  ProjectMilestoneResponseDto,
  ProjectResponseDto,
  ProjectTeamResponseDto,
  UpdateBaselineDto,
  UpdateGoalDto,
  UpdateProjectActivityDto,
  UpdateProjectDto,
  UpdateProjectExpenseDto,
  UpdateProjectMilestoneDto,
} from 'shared-general-libs';
import { ProjectRequestInterface } from '@ieShared/interfaces/project.interface';
import { CreateProjectTeamDto } from 'shared-general-libs/dto/project';
import {
  CreatePlanDto,
  PlanResponseDto,
  UpdatePlanDto,
} from 'shared-general-libs/dto/plan';
import { UserResponseDto } from 'shared-general-libs/dto/user';
import {
  BaselineTopicsMap,
  GoalTopicsMap,
} from '@ieShared/interfaces/api/materiality.interface';

@Injectable({
  providedIn: 'root',
})
export class MaterialityRepository {
  private readonly apiUrl: string = environment.materialitySurvey;

  constructor(private readonly http: HttpClient) {}

  getContacts(
    pagination: ContactsRequestInterface,
  ): Observable<PaginatedResponseDto<ContactResponseDto>> {
    return this.http.post<PaginatedResponseDto<ContactResponseDto>>(
      `${this.apiUrl}/contacts/search`,
      { ...removeEmptyFields(pagination) },
    );
  }

  createContact(payload: CreateContactDto) {
    return this.http.post<ContactResponseDto>(
      `${this.apiUrl}/contacts`,
      payload,
    );
  }

  editContact(payload: CreateContactDto, id: string) {
    return this.http.patch<ContactResponseDto>(
      `${this.apiUrl}/contacts/${id}`,
      payload,
    );
  }

  deleteContact(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/contacts/${id}`);
  }

  getContactById(id: string): Observable<ContactResponseDto> {
    return this.http.get<ContactResponseDto>(`${this.apiUrl}/contacts/${id}`);
  }

  getSurveys(
    pagination: SurveyRequestInterface,
  ): Observable<PaginatedResponseDto<SurveyResponseWithStatsDto>> {
    return this.http.get<PaginatedResponseDto<SurveyResponseWithStatsDto>>(
      `${this.apiUrl}/survey-participants/surveys-with-stats`,
      {
        params: removeEmptyFields(pagination),
      },
    );
  }

  getSurveyById(id: string): Observable<SurveyResponseDto> {
    return this.http.get<SurveyResponseDto>(`${this.apiUrl}/surveys/${id}`);
  }

  getSurveyStatsById(id: string): Observable<any> {
    return this.http
      .get<any>(`${this.apiUrl}/survey-participants/stats/${id}`)
      .pipe(
        map((data) => {
          if (!data) {
            return {
              [ImportanceType.BUSINESS]: {
                sent: 0,
                answered: 0,
                total: 0,
                notSent: 0,
                progress: 0,
                inProgress: 0,
              },
              [ImportanceType.STAKEHOLDERS]: {
                sent: 0,
                answered: 0,
                total: 0,
                notSent: 0,
                progress: 0,
                inProgress: 0,
              },
            };
          }
          const result: any = {};
          for (const type of Object.keys(data)) {
            result[type] = {
              ...data[type],
              notSent: data[type].total - data[type].sent,
              inProgress: data[type].sent - data[type].answered,
              progress:
                data[type].answered && data[type].sent
                  ? (data[type].answered / data[type].sent) * 100
                  : 0,
            };
          }
          return result;
        }),
      );
  }

  getSurveyParticipants(id: string): Observable<GetFormDataResponseDto> {
    return this.http.get<GetFormDataResponseDto>(
      `${this.apiUrl}/survey-forms/${id}`,
    );
  }

  getSurveyParticipantsList(
    id: string,
    pagination: ParticipantsPaginationInterface,
  ): Observable<PaginatedResponseDto<ContactResponseDto>> {
    return this.http.get<PaginatedResponseDto<ContactResponseDto>>(
      `${this.apiUrl}/survey-participants/${id}`,
      {
        params: removeEmptyFields(pagination),
      },
    );
  }

  getAggregatedStats(id: string): Observable<any> {
    return this.http.get<PaginatedResponseDto<ContactResponseDto>>(
      `${this.apiUrl}/answers/aggregate/${id}`,
    );
  }

  getAggregatedByContacts(
    id: string,
    topicId: string,
    pagination: any,
  ): Observable<any> {
    return this.http.get<PaginatedResponseDto<ContactResponseDto>>(
      `${this.apiUrl}/answers/average-by-contacts/${id}/${topicId}`,
      {
        params: removeEmptyFields(pagination),
      },
    );
  }

  saveSurveyAnswer(id: string, payload: CreateAnswerDto): Observable<void> {
    return this.http.post<void>(
      `${this.apiUrl}/survey-forms/answer/${id}`,
      payload,
    );
  }

  finalize(id: string, topics: string[]): Observable<void> {
    return this.http.patch<void>(`${this.apiUrl}/surveys/finalize/${id}`, {
      topics,
    });
  }

  sendSurvey(
    surveyId: string,
    importanceType: ImportanceType,
  ): Observable<void> {
    return this.http.post<void>(
      `${this.apiUrl}/survey-participants/send/${surveyId}`,
      {
        importanceType,
      },
    );
  }

  createSurvey(payload: CreateSurveyDto): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/surveys`, payload);
  }

  addStakeholders(
    id: string,
    payload: ManageSurveyParticipantsDto,
  ): Observable<void> {
    return this.http.post<void>(
      `${this.apiUrl}/survey-participants/manage-many/${id}`,
      payload,
    );
  }

  getContactsAnswer(surveyId: string, contactIds: string[]): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/answers/${surveyId}`, {
      params: removeEmptyFields({
        page: 1,
        itemsPerPage: 1000,
        contacts: contactIds,
      }),
    });
  }

  updateSurvey(id: string, payload: UpdateSurveyDto): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/surveys/${id}`, payload);
  }

  updateSurveyReminderDay(
    id: string,
    payload: UpdateSurveyEndDateDto,
  ): Observable<void> {
    return this.http.patch<void>(
      `${this.apiUrl}/survey-reminders/update-end-date/${id}`,
      payload,
    );
  }

  getProjects(pagination: ProjectRequestInterface) {
    return this.http
      .get<
        PaginatedResponseDto<ProjectResponseDto>
      >(`${this.apiUrl}/projects`, { params: removeEmptyFields(pagination) })
      .pipe(
        map((response) => {
          return {
            ...response,
            items: response.items.map((project) => {
              return {
                ...project,
                ownerId: project.owner?.id || null,
                facilityNames: project.facilities,
                facilities: project.facilities?.map(
                  (facility: any) => facility.id,
                ),
              };
            }),
          };
        }),
      );
  }

  getProjectById(id: string) {
    return this.http.get<ProjectResponseDto>(`${this.apiUrl}/projects/${id}`);
  }

  createProject(payload: CreateProjectDto) {
    return this.http.post<ProjectResponseDto>(
      `${this.apiUrl}/projects`,
      payload,
    );
  }

  updateProject(id: string, payload: UpdateProjectDto) {
    return this.http.put<ProjectResponseDto>(
      `${this.apiUrl}/projects/${id}`,
      payload,
    );
  }

  updateProjectStatus(id: string, payload: UpdateProjectDto) {
    return this.http.put<ProjectResponseDto>(
      `${this.apiUrl}/projects/${id}/status`,
      payload,
    );
  }

  getProjectMilestones(projectId: string) {
    return this.http.get<ProjectMilestoneResponseDto[]>(
      `${this.apiUrl}/project-milestones`,
      { params: { projectId } },
    );
  }

  getProjectMilestoneById(id: string) {
    return this.http.get<ProjectMilestoneResponseDto>(
      `${this.apiUrl}/project-milestones/${id}`,
    );
  }

  createProjectMilestone(payload: CreateProjectMilestoneDto) {
    return this.http.post<ProjectMilestoneResponseDto>(
      `${this.apiUrl}/project-milestones`,
      payload,
    );
  }

  updateProjectMilestone(id: string, payload: UpdateProjectMilestoneDto) {
    return this.http.put<ProjectMilestoneResponseDto>(
      `${this.apiUrl}/project-milestones/${id}`,
      payload,
    );
  }

  deleteProjectMilestone(id: string) {
    return this.http.delete<void>(`${this.apiUrl}/project-milestones/${id}`);
  }

  getProjectActivities(projectId: string) {
    return this.http.get<ProjectActivityResponseDto[]>(
      `${this.apiUrl}/project-activities`,
      { params: { projectId } },
    );
  }

  getProjectActivityById(id: string) {
    return this.http.get<ProjectActivityResponseDto>(
      `${this.apiUrl}/project-activities/${id}`,
    );
  }

  createProjectActivity(payload: CreateProjectActivityDto) {
    return this.http.post<ProjectActivityResponseDto>(
      `${this.apiUrl}/project-activities`,
      payload,
    );
  }

  updateProjectActivity(id: string, payload: UpdateProjectActivityDto) {
    return this.http.put<ProjectActivityResponseDto>(
      `${this.apiUrl}/project-activities/${id}`,
      payload,
    );
  }

  deleteProjectActivity(id: string) {
    return this.http.delete<void>(`${this.apiUrl}/project-activities/${id}`);
  }

  getProjectExpenses(projectId: string) {
    return this.http.get<ProjectExpenseResponseDto[]>(
      `${this.apiUrl}/project-expenses`,
      { params: { projectId } },
    );
  }

  getProjectExpenseById(id: string) {
    return this.http.get<ProjectExpenseResponseDto>(
      `${this.apiUrl}/project-expenses/${id}`,
    );
  }

  createProjectExpense(payload: CreateProjectExpenseDto) {
    return this.http.post<ProjectExpenseResponseDto>(
      `${this.apiUrl}/project-expenses`,
      payload,
    );
  }

  updateProjectExpense(id: string, payload: UpdateProjectExpenseDto) {
    return this.http.put<ProjectExpenseResponseDto>(
      `${this.apiUrl}/project-expenses/${id}`,
      payload,
    );
  }

  deleteProjectExpense(id: string) {
    return this.http.delete<void>(`${this.apiUrl}/project-expenses/${id}`);
  }

  getTeams(projectId: string) {
    return this.http.get<ProjectTeamResponseDto>(
      `${this.apiUrl}/project-teams`,
      {
        params: { projectId },
      },
    );
  }

  getTeamParticipants(projectId: string) {
    return this.http.get<ProjectTeamResponseDto>(
      `${this.apiUrl}/projects/${projectId}/participants`,
    );
  }

  getTeamById(id: string) {
    return this.http.get<ProjectTeamResponseDto>(
      `${this.apiUrl}/project-teams/${id}`,
    );
  }

  createTeam(payload: CreateProjectTeamDto) {
    return this.http.post<ProjectTeamResponseDto>(
      `${this.apiUrl}/project-teams`,
      payload,
    );
  }

  getPlans(queryParams: any) {
    return this.http.get<PaginatedResponseDto<PlanResponseDto>>(
      `${this.apiUrl}/plans`,
      {
        params: removeEmptyFields(queryParams),
      },
    );
  }

  getPlanById(id: string) {
    return this.http.get<PlanResponseDto>(`${this.apiUrl}/plans/${id}`);
  }

  editPlan(id: string, payload: UpdatePlanDto) {
    return this.http.put<PlanResponseDto>(
      `${this.apiUrl}/plans/${id}`,
      payload,
    );
  }

  createPlan(payload: CreatePlanDto) {
    return this.http.post<PlanResponseDto>(`${this.apiUrl}/plans`, payload);
  }

  deletePlan(id: string) {
    return this.http.delete<any>(`${this.apiUrl}/plans/${id}`);
  }

  getBaselineStats() {
    return this.http.get<BaselineTopicsMap>(
      `${this.apiUrl}/baselines/topic-statistics`,
    );
  }

  getBaselineStatsByTopic(topicKey: string) {
    return this.http.get<BaselineTopicStatisticsDto>(
      `${this.apiUrl}/baselines/topic-statistics/${topicKey}`,
    );
  }

  getBaselineById(id: string) {
    return this.http.get<BaselineResponseDto>(`${this.apiUrl}/baselines/${id}`);
  }

  createBaseline(queryParams: CreateBaselineDto) {
    return this.http.post<BaselineResponseDto>(
      `${this.apiUrl}/baselines`,
      queryParams,
    );
  }

  updateBaseline(id: string, payload: UpdateBaselineDto) {
    return this.http.put<BaselineResponseDto>(
      `${this.apiUrl}/baselines/${id}`,
      payload,
    );
  }

  makeInProgressBaseline(id: string) {
    return this.http.put<void>(
      `${this.apiUrl}/baselines/${id}/make-in-progress`,
      {},
    );
  }

  completeBaseline(id: string) {
    return this.http.put<void>(`${this.apiUrl}/baselines/${id}/complete`, {});
  }

  deleteBaseline(id: string) {
    return this.http.delete<void>(`${this.apiUrl}/baselines/${id}`);
  }

  getBaselineList(payload: FilterBaselinesDto) {
    return this.http.get<PaginatedResponseDto<BaselineResponseDto>>(
      `${this.apiUrl}/baselines`,
      {
        params: removeEmptyFields(payload),
      },
    );
  }

  getGoalsStats() {
    return this.http.get<GoalTopicsMap>(
      `${this.apiUrl}/goals/topic-statistics`,
    );
  }

  getGoalStatsByTopic(topicKey: string) {
    return this.http.get<GoalTopicStatisticsDto>(
      `${this.apiUrl}/goals/topic-statistics/${topicKey}`,
    );
  }

  getGoalList(queryParams: FilterGoalsDto) {
    return this.http.get<GoalResponseDto[]>(`${this.apiUrl}/goals`, {
      params: removeEmptyFields(queryParams),
    });
  }

  getGoalReviewList(queryParams: FilterGoalsDto) {
    return this.http.get<PaginatedResponseDto<GoalResponseDto>>(
      `${this.apiUrl}/goals/review/all`,
      {
        params: removeEmptyFields(queryParams),
      },
    );
  }

  sendGoalForReview(goalId: string) {
    return this.http.post<void>(
      `${this.apiUrl}/goals/${goalId}/send-review`,
      {},
    );
  }

  checkGoalForActivate(goalId: string) {
    return this.http.get<boolean>(
      `${this.apiUrl}/goals/${goalId}/check-approved`,
      {},
    );
  }

  finalizeGoal(goalId: string) {
    return this.http.post<void>(`${this.apiUrl}/goals/${goalId}/activate`, {});
  }

  autoAllocate(parentGoalId: string, payload: AutoAllocateDto) {
    return this.http.post<GoalResponseDto[]>(
      `${this.apiUrl}/goals/${parentGoalId}/auto-allocate`,
      payload,
    );
  }

  reAutoAllocate(parentGoalId: string, payload: AutoAllocateDto) {
    return this.http.post<GoalResponseDto[]>(
      `${this.apiUrl}/goals/${parentGoalId}/reallocate`,
      payload,
    );
  }

  applyAllocation(parentGoalId: string, payload: any) {
    return this.http.post<GoalResponseDto[]>(
      `${this.apiUrl}/goals/${parentGoalId}/apply`,
      payload,
    );
  }

  getFacilitiesAllocate(goalId: string) {
    return this.http.get<any[]>(
      `${this.apiUrl}/goals/${goalId}/facilities-for-allocation`,
      {},
    );
  }

  getGoalById(id: string) {
    return this.http.get<GoalResponseDto>(`${this.apiUrl}/goals/${id}`);
  }

  createGoal(payload: CreateGoalDto) {
    return this.http.post<any>(`${this.apiUrl}/goals`, payload);
  }

  allocateCustomGoal(payload: CustomAllocationDto) {
    return this.http.post<any>(`${this.apiUrl}/goals/custom-allocate`, payload);
  }

  editAllocateCustomGoal(id: string, parentId: string, payload: any) {
    return this.http.patch<any>(
      `${this.apiUrl}/goals/${parentId}/custom-allocations/${id}`,
      payload,
    );
  }

  updateGoal(id: string, payload: UpdateGoalDto) {
    return this.http.put<any>(`${this.apiUrl}/goals/${id}`, payload);
  }

  confirmGoal(id: string, payload: ConfirmGoalDto) {
    return this.http.post<any>(`${this.apiUrl}/goals/${id}/confirm`, payload);
  }

  deleteGoal(id: string) {
    return this.http.delete<any>(`${this.apiUrl}/goals/${id}`);
  }

  deleteAutoAllocations(id: string) {
    return this.http.delete<any>(`${this.apiUrl}/goals/${id}/auto-allocations`);
  }

  deleteCustomAllocations(id: string) {
    return this.http.delete<any>(
      `${this.apiUrl}/goals/${id}/custom-allocations`,
    );
  }

  getGoalOwners() {
    return this.http.get<UserResponseDto[]>(`${this.apiUrl}/goals/owners`);
  }

  getGoalApprovers() {
    return this.http.get<UserResponseDto[]>(`${this.apiUrl}/goals/approvers`);
  }
}
